import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BloqueoProgramacionRepository,
  VerificacionBloqueoResponse,
  BloquearOrdenRequest,
  DesbloquearOrdenesRequest,
  BloqueoResponse
} from 'src/app/domain/service/bloqueo-programacion/bloqueo-programacion-repository';

/**
 * Adaptador HTTP: HttpBloqueoProgramacionRepository
 *
 * Implementa el puerto BloqueoProgramacionRepository para comunicación HTTP con servicios-ms.
 * El proxy reenvía /api/servicios → http://localhost:8080
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto BloqueoProgramacionRepository del dominio
 *
 * Endpoints:
 * - GET /api/servicios/bloqueo-programacion/verificar
 * - POST /api/servicios/bloqueo-programacion/bloquear
 * - POST /api/servicios/bloqueo-programacion/desbloquear
 *
 * @author GIROP - Migración GWT a Angular
 */
@Injectable({ providedIn: 'root' })
export class HttpBloqueoProgramacionRepository extends BloqueoProgramacionRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/bloqueo-programacion`;

  /**
   * {@inheritDoc}
   * GET /api/servicios/bloqueo-programacion/verificar?nmconsecutivo={}&cdUsuario={}
   *
   * @legacy FN_SNTAREA_BLOQ_PARA_USR
   */
  override async verificarBloqueo(
    nmconsecutivo: number,
    cdUsuario: number
  ): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<VerificacionBloqueoResponse>(
        `${this.baseUrl}/verificar`,
        { params: { nmconsecutivo, cdUsuario } }
      )
    );
    return response.bloqueada;
  }

  /**
   * {@inheritDoc}
   * POST /api/servicios/bloqueo-programacion/bloquear
   *
   * @legacy ProgramarServiciosActivity.bloquearRegistros()
   */
  override async bloquearOrden(request: BloquearOrdenRequest): Promise<BloqueoResponse> {
    return await firstValueFrom(
      this.http.post<BloqueoResponse>(
        `${this.baseUrl}/bloquear`,
        request
      )
    );
  }

  /**
   * {@inheritDoc}
   * POST /api/servicios/bloqueo-programacion/desbloquear
   *
   * @legacy UtilitiesOnServer.unBlockRecordsSessionUsrOption()
   */
  override async desbloquearOrdenes(request: DesbloquearOrdenesRequest): Promise<BloqueoResponse> {
    // Usar SCHEDULE como opción por defecto si no se especifica
    const requestWithDefault: DesbloquearOrdenesRequest = {
      ...request,
      opcionBloqueo: request.opcionBloqueo || 'SCHEDULE'
    };

    return await firstValueFrom(
      this.http.post<BloqueoResponse>(
        `${this.baseUrl}/desbloquear`,
        requestWithDefault
      )
    );
  }
}
